import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Form from "react-bootstrap/Form";
import Spinner from "react-bootstrap/Spinner";
import Button from "react-bootstrap/Button"
import { useMsal, useAccount } from "@azure/msal-react";
import axios from "axios";
import authConfig from "../../authConfig";
import AddFormPopup from "./AddFormPopup";

function FormSelectorDropdown({
    value = "",
    onChange,
    onOpen,
    required = false,
    forMember = false, // true -> use member endpoint (cookie auth), false -> use token-acquired endpoint
    includeAddButton = false,
    includeOpenButton = false,
    id = "form-selector",
}) {
    const { instance, accounts } = useMsal();
    const account = useAccount(accounts[0] || {});
    const [forms, setForms] = useState([]);
    const [selected, setSelected] = useState(value || "");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setSelected(value || "");
    }, [value]);

    useEffect(() => {
        fetchForms();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [forMember]);

    const fetchForms = async () => {
        setLoading(true);
        setError(null);
        try {
            let data = [];
            if (forMember) {
                // Cookie-authenticated endpoint (members)
                const res = await fetch(authConfig.api.urls.getMemberFormListForMember, {
                    credentials: "include",
                });
                if (!res.ok) throw new Error(`Error fetching forms: ${res.statusText}`);
                data = await res.json();
            } else {
                // Authenticated API via MSAL token
                const tokenResponse = await instance.acquireTokenSilent({
                    scopes: authConfig.api.scopes,
                    account: account,
                });
                const client = axios.create({
                    headers: { Authorization: `Bearer ${tokenResponse.accessToken}` },
                });
                const res = await client.get(authConfig.api.urls.getMemberFormList);
                data = res.data;
            }
            // Normalize shape: API may return { Id, Name } or { id, name }
            const mapped = (data || []).map((o) => ({
                id: o.Id || o.id,
                name: o.Name || o.name,
            }));
            setForms(mapped);
        } catch (err) {
            console.error(err);
            setError(err?.message || "Error fetching forms");
            setForms([]);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const newVal = e.target.value || "";
        setSelected(newVal);
        if (onChange) onChange(newVal === "" ? null : newVal);
    };

    const handleAdded = async (newId) => {
        // refresh list and optionally select the newly added form
        await fetchForms();
        if (newId) {
            setSelected(newId);
            if (onChange) onChange(newId);
        }
    };

    const handleOnOpen = () => {
        if (onOpen) {
            onOpen(selected);
        }
    }

    return (
        <div className="d-flex align-items-center gap-2">
            {loading ? (
                <Spinner animation="border" size="sm" />
            ) : (
                <Form.Select id={id} value={selected || ""} onChange={handleChange} required={required}>
                    <option value="">{/* explicit empty option */}-- Select --</option>
                    {forms.map((f) => (
                        <option key={f.id} value={f.id}>
                            {f.name}
                        </option>
                    ))}
                </Form.Select>
            )}
            {includeAddButton && (
                <div>
                    <AddFormPopup onAdded={handleAdded} label="Add"/>
                </div>
            )}
            {error ? <div className="text-danger small ms-2">{error}</div> : null}
        </div>
    );
}

FormSelectorDropdown.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func,
    onOpen: PropTypes.func,
    required: PropTypes.bool,
    forMember: PropTypes.bool,
    includeAddButton: PropTypes.bool,
    includeOpenButton: PropTypes.bool,
    id: PropTypes.string,
};

export default FormSelectorDropdown;